// Клиент API Product (API.md): Bearer access-токен, ротация refresh-токена, ошибки RFC 9457, заголовок X-Client.
import type { BrovkaDB } from "./db.ts";
import { uuidv7 } from "./uuid.ts";

export type Tokens = { access_token: string; refresh_token: string };
export type Problem = { status: number; code: string; title: string; detail?: string; min_version?: string };

export class ApiError extends Error {
  constructor(public readonly problem: Problem) {
    super(problem.title);
  }
  get code() {
    return this.problem.code;
  }
  get status() {
    return this.problem.status;
  }
}

/** Нет сети или сервер недоступен — приложение продолжает работать офлайн. */
export class OfflineError extends Error {}

type Opts = { auth?: boolean; idempotencyKey?: string; signal?: AbortSignal };

export class Api {
  private refreshing: Promise<boolean> | null = null;
  constructor(
    private readonly db: BrovkaDB,
    private readonly base = "",
    private readonly fetchFn: typeof fetch = (...a) => fetch(...a),
  ) {}

  async tokens(): Promise<Tokens | undefined> {
    return this.db.get<Tokens>("tokens");
  }

  async deviceId(): Promise<string> {
    let id = await this.db.get<string>("deviceId");
    if (!id) {
      id = uuidv7();
      await this.db.set("deviceId", id);
    }
    return id;
  }

  /** Анонимный аккаунт и устройство при первом запуске (без ПДн). */
  async ensureDevice(): Promise<Tokens> {
    const t = await this.tokens();
    if (t) return t;
    for (let attempt = 0; attempt < 2; attempt++) {
      const id = await this.deviceId();
      try {
        const r = await this.request<Tokens & { account: { id: string } }>("POST", "/v1/devices", { device: { id, platform: "web", app_version: __APP_VERSION__ } }, { auth: false });
        await this.db.set("tokens", { access_token: r.access_token, refresh_token: r.refresh_token });
        await this.db.set("accountId", r.account.id);
        return r;
      } catch (e) {
        // Идентификатор уже занят (например, после очистки токенов) — новый идентификатор устройства.
        if (e instanceof ApiError && e.code === "device.exists") {
          await this.db.del("deviceId");
          continue;
        }
        throw e;
      }
    }
    throw new OfflineError("device");
  }

  private async refresh(): Promise<boolean> {
    if (!this.refreshing) {
      this.refreshing = (async () => {
        const t = await this.tokens();
        if (!t) return false;
        try {
          const r = await this.request<Tokens>("POST", "/v1/auth/refresh", { refresh_token: t.refresh_token }, { auth: false });
          await this.db.set("tokens", { access_token: r.access_token, refresh_token: r.refresh_token });
          return true;
        } catch (e) {
          if (e instanceof ApiError && e.status === 401) {
            // Цепочка отозвана или аккаунт удалён — начать с новым анонимным устройством.
            await this.db.del("tokens");
          }
          return false;
        } finally {
          this.refreshing = null;
        }
      })();
    }
    return this.refreshing;
  }

  async request<T>(method: string, path: string, body?: unknown, opts: Opts = {}): Promise<T> {
    const auth = opts.auth ?? true;
    for (let attempt = 0; attempt < 2; attempt++) {
      const headers: Record<string, string> = { "X-Client": `brovka-client/${__APP_VERSION__} (web)` };
      if (body !== undefined) headers["Content-Type"] = "application/json";
      if (opts.idempotencyKey) headers["Idempotency-Key"] = opts.idempotencyKey;
      if (auth) {
        const t = await this.tokens();
        if (!t) throw new ApiError({ status: 401, code: "auth.missing_token", title: "Нет токена" });
        headers.Authorization = `Bearer ${t.access_token}`;
      }
      let resp: Response;
      try {
        resp = await this.fetchFn(this.base + path, { method, headers, body: body === undefined ? undefined : JSON.stringify(body), signal: opts.signal });
      } catch {
        throw new OfflineError(path);
      }
      if (resp.status === 401 && auth && attempt === 0 && (await this.refresh())) continue;
      if (resp.status >= 500 || resp.status === 0) throw new OfflineError(`${path}: ${resp.status}`);
      const text = await resp.text();
      const data = text ? JSON.parse(text) : undefined;
      if (!resp.ok) throw new ApiError({ status: resp.status, code: data?.code ?? "http", title: data?.title ?? resp.statusText, detail: data?.detail, min_version: data?.min_version });
      return data as T;
    }
    throw new ApiError({ status: 401, code: "auth.invalid_token", title: "Требуется вход" });
  }

  /** Файл как байты (контент релиза). */
  async bytes(path: string): Promise<Uint8Array> {
    let resp: Response;
    try {
      resp = await this.fetchFn(this.base + path);
    } catch {
      throw new OfflineError(path);
    }
    if (!resp.ok) throw new ApiError({ status: resp.status, code: "content.fetch", title: `${path}: ${resp.status}` });
    return new Uint8Array(await resp.arrayBuffer());
  }
}
